import React from 'react';
import { Clock, X } from 'lucide-react';

type OrderItem = {
  menuItemName?: string;
  quantity?: number;
  totalPrice?: number;
  status?: string;
};

type Order = {
  id?: string;
  createdAt?: { toDate?: () => Date } | Date;
  total?: number;
  items?: OrderItem[];
};

type Props = {
  open: boolean;
  tableNumber: string | undefined;
  orders: Order[];
  onClose: () => void;
  getOrderStatusColor: (status: string) => string;
  getOrderStatusText: (status: string) => string;
};

const formatCreatedAt = (createdAt: Order['createdAt']): string => {
  if (!createdAt) return '';
  const d =
    typeof createdAt === 'object' && createdAt !== null && 'toDate' in createdAt && typeof createdAt.toDate === 'function'
      ? createdAt.toDate()
      : createdAt instanceof Date
        ? createdAt
        : new Date(createdAt as unknown as string);
  return d.toLocaleString();
};

const PublicMenuOrderStatusModal: React.FC<Props> = ({
  open,
  tableNumber,
  orders,
  onClose,
  getOrderStatusColor,
  getOrderStatusText,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-zinc-200 p-4">
          <div className="flex items-center gap-2">
            <Clock className="h-6 w-6 text-blue-600" />
            <h2 className="text-lg font-semibold text-zinc-900">Orders · Table {tableNumber}</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-zinc-400 hover:bg-zinc-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {orders.length === 0 ? (
            <p className="text-center text-sm text-zinc-500">No active orders.</p>
          ) : (
            <ul className="space-y-3">
              {orders.map((order) => (
                <li key={order.id} className="rounded-lg border-l-4 border-blue-500 bg-zinc-50 p-3">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-zinc-900">Order #{order.id}</p>
                      <p className="text-xs text-zinc-500">{formatCreatedAt(order.createdAt)}</p>
                    </div>
                    <span className="font-bold text-zinc-900">₹{(order.total ?? 0).toFixed(2)}</span>
                  </div>
                  <ul className="mb-2 space-y-1">
                    {order.items?.map((item, index) => (
                      <li key={index} className="flex items-center justify-between border-b border-zinc-200 py-1 text-sm last:border-0">
                        <span className="font-medium text-zinc-800">{item.menuItemName}</span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${getOrderStatusColor(item.status ?? '')}`}>
                          {getOrderStatusText(item.status ?? '')}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between border-t border-zinc-200 pt-2 text-xs">
                    <span className="font-medium text-zinc-600">Status</span>
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${
                        order.items?.every((i) => i.status === 'completed')
                          ? 'bg-green-100 text-green-800'
                          : order.items?.some((i) => i.status === 'preparing')
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {order.items?.every((i) => i.status === 'completed')
                        ? 'Ready'
                        : order.items?.some((i) => i.status === 'preparing')
                          ? 'Preparing'
                          : 'Received'}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border-t border-zinc-200 bg-zinc-50 px-4 py-2">
          <p className="text-center text-[10px] text-zinc-500">Updates in real time.</p>
        </div>
      </div>
    </div>
  );
};

export default PublicMenuOrderStatusModal;
